import { Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { AdressePhysiqueBancaireModification } from '../../api/model/adresse-physique-bancaire-modification.model';
import { RequeteModifierCoordonneesBancaire } from '../../api/model/requete-modifier-coordonnees-bancaire.model';
import { FormulaireCoordonneesBancaires } from '../../models/formulaire/formulaire-coordonnees-bancaires.model';
import { FormulaireBancaireService } from '../formulaire-bancaire/formulaire-bancaire.service';
import { GlobalStoreService } from '../global-store/global-store.service';
import { ParticulierBancaire } from 'src/app/api/model/particulier-bancaire.model';
import { DateUtil } from 'src/app/util/date-util';

@Injectable({
  providedIn: 'root',
})
export class RequeteCoordonneesBancaireService {
  dateUtil = new DateUtil();

  constructor(
    private readonly formulaireBancaireService: FormulaireBancaireService,
    private readonly globalStoreService: GlobalStoreService
  ) {}

  construireRequeteModifierCoordonnees(): RequeteModifierCoordonneesBancaire {
    const form: FormGroup<FormulaireCoordonneesBancaires> = this.formulaireBancaireService.getForm();
    const courrielModifie = form?.value.courriel ?? '';
    const adresseModifie = this.construireAdresse(form);

    // Nettoie le code postal (enlève l'espace)
    if (adresseModifie?.codePostal) {
      adresseModifie.codePostal = adresseModifie.codePostal.replaceAll(' ', '');
    }

    const membre: ParticulierBancaire = this.globalStoreService.getValeurCoordonneesMembre();

    const empreinteCourriel = membre?.courriel?.empreinte ? membre.courriel.empreinte : null;
    const empreinteAdresse = membre?.adresseCourante?.empreinte ? membre.adresseCourante.empreinte : null;
    const empreintePostdatee = membre?.adressePostdatee?.empreinte ? membre.adressePostdatee.empreinte : null;

    if (adresseModifie) {
      adresseModifie.empreinte = empreinteAdresse;
      adresseModifie.empreintePostdatee = empreintePostdatee;
    }

    const payload: RequeteModifierCoordonneesBancaire = {
      courriel: {
        adresseCourriel: courrielModifie,
        empreinte: empreinteCourriel,
      },
      adresse: adresseModifie,
    };

    return payload;
  }

  private getCodeSubdivisionPays(form: FormGroup<FormulaireCoordonneesBancaires>): string {
    const pays = form.value.codePays;
    const subdivision = form.value.province;
    const isoFormat = /^[A-Z]{2}-[A-Z0-9]{1,3}$/;

    if (pays === 'CA' || pays === 'US' || pays === 'MX') {
      return subdivision ? `${pays}-${subdivision}` : null;
    }
    // Si présent, le code d'état doit être valide selon ISO 3166-2
    if (subdivision && isoFormat.test(`${pays}-${subdivision}`)) {
      return `${pays}-${subdivision}`;
    }
    return null;
  }

  // Retourne null si la date est aujourd'hui
  private getDateDebutUtilisation(dateChangement: string): string | null {
    if (this.dateUtil.estMemeJour(dateChangement)) {
      return null;
    }
    return dateChangement;
  }

  private construireAdresse(
    form: FormGroup<FormulaireCoordonneesBancaires>
  ): AdressePhysiqueBancaireModification | null {
    if (form.value.utiliserMemeAdresse) {
      return null;
    }

    const codeSubdivisionPays = this.getCodeSubdivisionPays(form);

    // Nettoie le code postal (enlève l'espace)
    const codePostal = form.value.codePostal ? form.value.codePostal.replaceAll(' ', '') : null;

    return {
      numeroCivique: form.value.numeroCivique ? form.value.numeroCivique : null,
      nomRue: form.value.rue ? form.value.rue : null,
      numeroUnite: form.value.appartement ? form.value.appartement : null,
      nomMunicipalite: form.value.ville ? form.value.ville : null,
      codeSubdivisionPays: codeSubdivisionPays,
      codePostal: codePostal,
      codePays: form.value.codePays ? form.value.codePays : null,
      dateDebutUtilisation: this.getDateDebutUtilisation(form.value.dateChangement),
    };
  }
}
